import React from 'react';
import { TrendingUp, Clock, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Workstream, Synergy } from '../../types/integration';
import { formatCurrency } from '../../utils/formatters';

interface IntegrationMetricsProps {
  workstreams: Workstream[];
  synergies: Synergy[];
}

export default function IntegrationMetrics({ workstreams, synergies }: IntegrationMetricsProps) {
  const totalWorkstreams = workstreams.length;
  const completedWorkstreams = workstreams.filter(w => w.status === 'completed').length;
  const blockedWorkstreams = workstreams.filter(w => w.status === 'blocked').length;
  const overallProgress = totalWorkstreams 
    ? (workstreams.reduce((sum, w) => sum + w.progress, 0) / totalWorkstreams)
    : 0;

  const totalSynergies = synergies.reduce((sum, s) => sum + s.targetValue, 0);
  const realizedSynergies = synergies.reduce((sum, s) => sum + s.actualValue, 0);
  const synergyRealization = totalSynergies ? (realizedSynergies / totalSynergies) * 100 : 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      <div className="bg-white p-4 rounded-lg shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-gray-700">Overall Progress</h3>
          <TrendingUp className="w-5 h-5 text-blue-500" />
        </div>
        <p className="text-2xl font-bold text-gray-900">{overallProgress.toFixed(1)}%</p>
        <p className="mt-1 text-sm text-gray-500">Integration completion</p>
      </div>

      <div className="bg-white p-4 rounded-lg shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-gray-700">Workstreams</h3>
          <Clock className="w-5 h-5 text-purple-500" />
        </div>
        <p className="text-2xl font-bold text-gray-900">{completedWorkstreams}/{totalWorkstreams}</p>
        <p className="mt-1 text-sm text-gray-500">Completed workstreams</p>
      </div>

      <div className="bg-white p-4 rounded-lg shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-gray-700">Synergy Realization</h3>
          <CheckCircle2 className="w-5 h-5 text-green-500" />
        </div>
        <p className="text-2xl font-bold text-gray-900">{formatCurrency(realizedSynergies)}</p>
        <p className="mt-1 text-sm text-gray-500">{synergyRealization.toFixed(1)}% of target</p>
      </div>

      <div className="bg-white p-4 rounded-lg shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-gray-700">Blocked Items</h3>
          <AlertTriangle className="w-5 h-5 text-red-500" />
        </div>
        <p className="text-2xl font-bold text-gray-900">{blockedWorkstreams}</p>
        <p className="mt-1 text-sm text-gray-500">Need attention</p>
      </div>
    </div>
  );
}